import { useState, useEffect } from "react"
import Header from "../common/Header.js"
import Sidebar from "../common/Sidebar.js"
import dashboardService from "../../services/DashboardService.js"
import Alert from "../common/Alert.js"
import {
  Package, FileText, Image, TrendingUp,
  Filter, X, Box, Container, ClipboardCheck, Receipt,
  CheckCircle2, Landmark, Percent, Clock, DollarSign, IndianRupee, CreditCard,
  AlertCircle, ShieldCheck, Activity, Download, LayoutDashboard
} from "lucide-react"
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from 'recharts'

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#14B8A6']

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-gray-600">{p.name}:</span>
            <span className="font-bold text-gray-900">{p.value}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

const StatCard = ({ title, value, subtitle, icon: Icon, gradient, textLight }) => (
  <div className={`${gradient} rounded-md shadow-lg p-5 py-3 text-white relative overflow-hidden`}>
    <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 -translate-y-8 translate-x-8" />
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <p className={`${textLight} text-sm font-medium`}>{title}</p>
        <div className="bg-white bg-opacity-20 p-2.5 rounded-xl">
          <Icon size={22} />
        </div>
      </div>
      <h3 className="text-3xl font-bold">{value}</h3>
      {subtitle && <p className={`${textLight} text-xs mt-1`}>{subtitle}</p>}
    </div>
  </div>
)

const SectionHeader = ({ icon: Icon, title, color = "text-orange" }) => (
  <div className="flex items-center gap-2 mb-4 bg-gray-100 p-2 px-1">
    <div className="w-1 h-6 rounded-full bg-orange-500" />
    <Icon size={20} className={color} />
    <h2 className="text-lg font-semibold text-gray-600">{title}</h2>
  </div>
)

const ClientDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [filters, setFilters] = useState({ from_date: '', to_date: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [datePreset, setDatePreset] = useState('')

  useEffect(() => {
    const userRole = dashboardService.getCurrentUserRole()
    const userId = dashboardService.getCurrentUserId()
    console.log('[v0] ClientDashboard initialized - Role:', userRole, '- User ID:', userId)
    
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async (customFilters = filters) => {
    try {
      setLoading(true)
      const response = await dashboardService.getDashboardData(customFilters)
      setDashboardData(response.data)
    } catch (err) {
      Alert.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
    setDatePreset('')
  }

  const handleDatePresetChange = (preset) => {
    if (!preset) { clearFilters(); return }
    const presets = dashboardService.getDatePresets()
    const selected = presets[preset]
    if (selected) {
      const newFilters = { from_date: selected.from, to_date: selected.to }
      setFilters(newFilters)
      setDatePreset(preset)
      fetchDashboardData(newFilters)
    }
  }

  const applyFilters = () => { fetchDashboardData(); setShowFilters(false) }

  const clearFilters = () => {
    const cleared = { from_date: '', to_date: '' }
    setFilters(cleared)
    setDatePreset('')
    fetchDashboardData(cleared)
  }

  const exportBillingSummary = () => {
    const b = dashboardData?.billing
    if (!b) return
    const csv = [
      ['Metric', 'Value'],
      ['Total Invoices', b.total],
      ['Paid', b.paid],
      ['Unpaid', b.unpaid],
      ['Partial Paid', b.partial_paid],
      ['Total Billed', b.total_billed_amount],
      ['Total Paid', b.total_paid_amount],
      ['Outstanding Balance', b.total_balance_amount],
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'my_billing_summary.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const hasActiveFilters = filters.from_date || filters.to_date

  // ─── Chart helpers ──────────────────────────────────────────────────────
  const prepareAreaChartData = () => {
    if (!dashboardData?.records_by_date) return []
    const dateMap = new Map()
    Object.entries(dashboardData.records_by_date).forEach(([key, data]) => {
      data.forEach(item => {
        if (!dateMap.has(item.date)) dateMap.set(item.date, { date: item.date })
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        dateMap.get(item.date)[label] = item.count
      })
    })
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  const prepareRecordTypesChart = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box Survey', value: dashboardData.dry_box_survey?.total || 0 },
      { name: 'Tank Cleaning', value: dashboardData.tank_cleaning?.total || 0 },
      { name: 'On Hire', value: dashboardData.onhire?.total || 0 },
      { name: 'EI Receipt', value: dashboardData.ei_receipt?.total || 0 },
      { name: 'TI Report', value: dashboardData.ti_report?.total || 0 },
    ]
  }

  const prepareStatusChart = () => {
    if (!dashboardData) return []
    const totalPublished = (dashboardData.dry_box_survey?.published || 0) + (dashboardData.tank_cleaning?.published || 0) +
      (dashboardData.onhire?.published || 0) + (dashboardData.ei_receipt?.published || 0) + (dashboardData.ti_report?.published || 0)
    const totalUnpublished = totalRecords - totalPublished
    return [
      { name: 'Published', value: totalPublished, fill: '#10B981' },
      { name: 'Pending', value: totalUnpublished, fill: '#F59E0B' },
    ]
  }

  const prepareBillingStatusData = () => {
    const b = dashboardData?.billing
    if (!b) return []
    return [
      { name: 'Paid', value: b.paid, fill: '#10B981' },
      { name: 'Unpaid', value: b.unpaid, fill: '#EF4444' },
      { name: 'Partial', value: b.partial_paid, fill: '#F59E0B' },
    ]
  }

  const prepareBillingTrend = () => {
    if (!dashboardData?.records_by_date?.billing) return []
    return dashboardData.records_by_date.billing.map(item => ({ date: item.date, invoices: item.count }))
  }

  const totalRecords = (dashboardData?.dry_box_survey?.total || 0) + (dashboardData?.tank_cleaning?.total || 0) +
    (dashboardData?.onhire?.total || 0) + (dashboardData?.ei_receipt?.total || 0) + (dashboardData?.ti_report?.total || 0)

  const totalPublished = (dashboardData?.dry_box_survey?.published || 0) + (dashboardData?.tank_cleaning?.published || 0) +
    (dashboardData?.onhire?.published || 0) + (dashboardData?.ei_receipt?.published || 0) + (dashboardData?.ti_report?.published || 0)

  const totalPending = totalRecords - totalPublished
  const billing = dashboardData?.billing || {}
  const ti = dashboardData?.ti_report || {}

  if (loading && !dashboardData) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-gray-50 w-full">
          <div className="flex-1 flex flex-col">
            <main className="flex-1 p-6 overflow-auto">

              {/* ── Page header ── */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 className="text-2xl sm:text-2xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <LayoutDashboard className="text-orange" size={28} />
                    My Dashboard
                  </h1>
                </div>
                <button onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    hasActiveFilters ? 'bg-orange-500 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}>
                  <Filter size={16} />
                  Filters
                  {hasActiveFilters && <span className="bg-white text-orange-500 px-1.5 py-0.5 rounded-full text-xs font-bold">Active</span>}
                </button>
              </div>

              {/* ── Filter panel ── */}
              {showFilters && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-bold text-gray-800">Date Range Filter</h3>
                    <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
                    {[
                      { label: 'Date Preset', el: (
                        <select value={datePreset} onChange={e => handleDatePresetChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                          <option value="">Custom Range</option>
                          {['today','yesterday','last7Days','last30Days','last3Months','thisMonth','thisYear'].map(k => (
                            <option key={k} value={k}>{dashboardService.getDatePresets()[k]?.label}</option>
                          ))}
                        </select>
                      )},
                      { label: 'From Date', el: <input type="date" value={filters.from_date} onChange={e => handleFilterChange('from_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500" /> },
                      { label: 'To Date', el: <input type="date" value={filters.to_date} onChange={e => handleFilterChange('to_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500" /> },
                    ].map(({ label, el }) => (
                      <div key={label}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
                        {el}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={clearFilters} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">Clear</button>
                    <button onClick={applyFilters} className="px-4 py-2 bg-buttonBg text-white rounded-xl text-sm hover:bg-orange-600 font-medium">Apply Filters</button>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 1 – RECORDS SUMMARY
              ══════════════════════════════════════ */}
              <SectionHeader icon={Activity} title="My Records Overview" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Records" value={totalRecords} subtitle="All types combined" icon={FileText} gradient="bg-orange" textLight="text-orange-100" />
                <StatCard title="Published" value={totalPublished} subtitle="Ready for download" icon={CheckCircle2} gradient="bg-gradient-to-br from-green-500 to-green-600" textLight="text-green-100" />
                <StatCard title="Pending / Draft" value={totalPending} subtitle="In processing" icon={Clock} gradient="bg-gradient-to-br from-yellow-500 to-yellow-600" textLight="text-yellow-100" />
                <StatCard title="Total Images" value={dashboardData?.file_cabinet_images?.total || 0} subtitle="File cabinet" icon={Image} gradient="bg-gradient-to-br from-blue-500 to-blue-600" textLight="text-blue-100" />
              </div>

              {/* Assets */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 border-l-4 border-green-500 flex items-center gap-5">
                  <div className="p-3 bg-green-50 rounded-xl"><Box size={32} className="text-green-500" /></div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">My Tanks</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardData?.total_tanks || 0}</p>
                  </div>
                </div>
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 border-l-4 border-purple-500 flex items-center gap-5">
                  <div className="p-3 bg-purple-50 rounded-xl"><Container size={32} className="text-purple-500" /></div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">My Containers</p>
                    <p className="text-3xl font-bold text-gray-900">{dashboardData?.total_containers || 0}</p>
                  </div>
                </div>
              </div>

              {/* Record breakdown cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {[
                  { label: 'Dry Box Survey', data: dashboardData?.dry_box_survey, icon: FileText, color: 'text-orange-500', border: 'border-orange-500' },
                  { label: 'Tank Cleaning', data: dashboardData?.tank_cleaning, icon: ClipboardCheck, color: 'text-blue-500', border: 'border-blue-500' },
                  { label: 'On Hire', data: dashboardData?.onhire, icon: Package, color: 'text-green-500', border: 'border-green-500' },
                  { label: 'EI Receipt', data: dashboardData?.ei_receipt, icon: Receipt, color: 'text-purple-500', border: 'border-purple-500' },
                  { label: 'TI Report', data: dashboardData?.ti_report, icon: ShieldCheck, color: 'text-teal-500', border: 'border-teal-500' },
                ].map(({ label, data, icon: Icon, color, border }) => (
                  <div key={label} className={`bg-white rounded-sm shadow-sm border border-gray-100 p-5 border-l-4 ${border}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Icon size={16} className={color} />
                      <h4 className="font-semibold text-gray-700 text-xs">{label}</h4>
                    </div>
                    <p className="text-2xl font-semibold text-gray-600">{data?.total || 0}</p>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className="text-xs bg-green-50 text-green-700 px-2 py-0.5 rounded-full">✓ {data?.published || 0}</span>
                      <span className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full">○ {data?.unpublished || 0}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp size={18} className="text-orange" />
                    <h3 className="font-semibold text-gray-600">Records Trend</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareAreaChartData()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        {[['dry','#F97316'],['tank','#3B82F6'],['hire','#10B981'],['ei','#8B5CF6'],['ti','#14B8A6']].map(([k,c]) => (
                          <linearGradient key={k} id={`cg${k}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={c} stopOpacity={0.15}/>
                            <stop offset="95%" stopColor={c} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Area type="monotone" dataKey="Dry Box Survey" stroke="#F97316" strokeWidth={2} fill="url(#cgdry)" dot={{ r: 3, fill: '#F97316' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Tank Cleaning" stroke="#3B82F6" strokeWidth={2} fill="url(#cgtank)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Onhire" stroke="#10B981" strokeWidth={2} fill="url(#cghire)" dot={{ r: 3, fill: '#10B981' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Ei Receipt" stroke="#8B5CF6" strokeWidth={2} fill="url(#cgei)" dot={{ r: 3, fill: '#8B5CF6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Ti Report" stroke="#14B8A6" strokeWidth={2} fill="url(#cgti)" dot={{ r: 3, fill: '#14B8A6' }} activeDot={{ r: 5 }} connectNulls={true} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-600 mb-5">Record Status Overview</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={prepareStatusChart()} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={4} dataKey="value"
                        label={({ name, value, percent }) => percent > 0.05 ? `${name}: ${(percent * 100).toFixed(0)}%` : ''}>
                        {prepareStatusChart().map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-around mt-4">
                    {prepareStatusChart().map(d => (
                      <div key={d.name} className="text-center">
                        <div className="w-3 h-3 rounded-full mx-auto mb-1" style={{ background: d.fill }} />
                        <p className="text-sm font-bold text-gray-900">{d.value}</p>
                        <p className="text-xs text-gray-500">{d.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ══════════════════════════════════════
                  SECTION 2 – MY BILLING
              ══════════════════════════════════════ */}
              <SectionHeader icon={IndianRupee} title="My Billing Summary" color="text-green-600" />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Invoices" value={billing.total || 0} subtitle="All your invoices" icon={FileText} gradient="bg-gradient-to-br from-slate-600 to-slate-700" textLight="text-slate-300" />
                <StatCard title="Total Billed" value={`₹${(billing.total_billed_amount || 0).toLocaleString()}`} subtitle="Charged to you" icon={CreditCard} gradient="bg-gradient-to-br from-blue-500 to-blue-600" textLight="text-blue-100" />
                <StatCard title="Amount Paid" value={`₹${(billing.total_paid_amount || 0).toLocaleString()}`} subtitle="Payments made" icon={CheckCircle2} gradient="bg-gradient-to-br from-green-500 to-green-600" textLight="text-green-100" />
                <StatCard title="Outstanding" value={`₹${(billing.total_balance_amount || 0).toLocaleString()}`} subtitle="Amount due" icon={AlertCircle} gradient="bg-gradient-to-br from-red-500 to-red-600" textLight="text-red-100" />
              </div>

              {/* TDS + Bank Payment breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <StatCard title="Bank Payment" value={`₹${(billing.total_bank_payment || 0).toLocaleString()}`} subtitle="Paid via bank" icon={Landmark} gradient="bg-gradient-to-br from-indigo-500 to-indigo-700" textLight="text-indigo-200" />
                <StatCard title="TDS Deducted" value={`₹${(billing.total_tds || 0).toLocaleString()}`} subtitle="Tax deducted at source" icon={Percent} gradient="bg-gradient-to-br from-purple-500 to-purple-700" textLight="text-purple-200" />
              </div>

              {/* Invoice status strip */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Paid Invoices', count: billing.paid || 0, bg: 'bg-green-50 border-green-200', text: 'text-green-700', dot: 'bg-green-500' },
                  { label: 'Partially Paid', count: billing.partial_paid || 0, bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', dot: 'bg-yellow-500' },
                  { label: 'Unpaid', count: billing.unpaid || 0, bg: 'bg-red-50 border-red-200', text: 'text-red-700', dot: 'bg-red-500' },
                ].map(({ label, count, bg, text, dot }) => (
                  <div key={label} className={`border rounded-2xl p-5 ${bg}`}>
                    <div className={`flex items-center gap-2 mb-1 ${text}`}>
                      <div className={`w-2 h-2 rounded-full ${dot}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
                    </div>
                    <p className={`text-3xl font-bold ${text}`}>{count}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-semibold text-gray-600">Billing History</h3>
                    <button onClick={exportBillingSummary} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <Download size={13} /> Export
                    </button>
                  </div>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={prepareBillingTrend()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        <linearGradient id="gradClientBill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10B981" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="invoices" stroke="#10B981" strokeWidth={2.5} fill="url(#gradClientBill)" dot={{ r: 3, fill: '#10B981' }} name="Invoices" connectNulls={true} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-600 mb-4">Invoice Status</h3>
                  <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                      <Pie data={prepareBillingStatusData()} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={4} dataKey="value">
                        {prepareBillingStatusData().map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3 mt-4">
                    {prepareBillingStatusData().map(d => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: d.fill }} />
                          <span className="text-sm text-gray-600">{d.name}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{d.value}</span>
                      </div>
                    ))}
                  </div>
                  {billing.total_billed_amount > 0 && (
                    <div className="mt-5 pt-5 border-t border-gray-100">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-gray-500">Payment Progress</span>
                        <span className="font-bold text-gray-900">{((billing.total_paid_amount / billing.total_billed_amount) * 100).toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: `${Math.min((billing.total_paid_amount / billing.total_billed_amount) * 100, 100)}%` }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── File Cabinet Images ── */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Image size={18} className="text-orange" />
                  <h3 className="font-semibold text-gray-600">File Cabinet Images</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Dry Box', val: dashboardData?.file_cabinet_images?.dry_box, bg: 'bg-orange-50', text: 'text-orange-600' },
                    { label: 'Tank Cleaning', val: dashboardData?.file_cabinet_images?.tank_cleaning, bg: 'bg-blue-50', text: 'text-blue-600' },
                    { label: 'On Hire', val: dashboardData?.file_cabinet_images?.onhire, bg: 'bg-green-50', text: 'text-green-600' },
                    { label: 'EI Receipt', val: dashboardData?.file_cabinet_images?.ei_receipt, bg: 'bg-purple-50', text: 'text-purple-600' },
                    { label: 'Total', val: dashboardData?.file_cabinet_images?.total, bg: 'bg-gray-100', text: 'text-gray-900 font-bold' },
                  ].map(({ label, val, bg, text }) => (
                    <div key={label} className={`text-center p-4 ${bg} rounded-xl`}>
                      <p className={`text-2xl font-bold ${text}`}>{val || 0}</p>
                      <p className="text-xs text-gray-500 mt-1">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

            </main>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClientDashboard
